class AdvancedQRGeneratorService {
  static async generateAdvancedQR(config) {
    try {
      // RADICAL FIX: SWAP URL LOGIC COMPLETELY
      let qrContent = config?.data || config?.content;
      
      // Validate that we have content
      if (!qrContent || !qrContent.trim()) {
        throw new Error('No input text');
      }
      
      // NEW APPROACH: Instead of forcing trackable URL, use whatever is in config.data or config.content
      // The database migration has already swapped the logic so config.data should contain trackable URL
      console.log(`🔄 SWAPPED LOGIC: QR will embed: "${qrContent}"`);
      
      // Remove all the ultra-aggressive validation - let the database handle URL logic
      // The migration ensures content field contains trackable URLs

      // Enhanced configuration for logo embedding
      const advancedConfig = {
        width: config?.width || 512,
        height: config?.height || 512,
        type: "svg",
        data: qrContent, // Use whatever is provided - should be trackable URL from database
        qrOptions: {
          errorCorrectionLevel: config?.errorCorrectionLevel || 'H'
        }
      };

      // Advanced logo embedding with text integration
      if (config?.logoUrl || config?.embedText) {
        // For text embedding like "TAYLOR" - either direct text or logo converted to text
        if (config?.embedText || (config?.logoUrl && config?.logoStyle === 'text' && config?.logoText)) {
          const textToEmbed = config?.embedText || config?.logoText;
          advancedConfig.textOptions = {
            text: textToEmbed,
            font: config?.logoTextFont || config?.textFont || 'Arial, sans-serif',
            fontSize: config?.logoTextSize || config?.textSize || 24,
            fontWeight: config?.logoTextWeight || config?.textWeight || 'bold',
            color: config?.logoTextColor || config?.textColor || config?.foregroundColor || '#000000',
            position: config?.textPosition || 'center',
            margin: config?.textMargin || 8
          };
        }

        // Enhanced logo options with better CORS handling - only for embedded style
        if (config?.logoUrl && config?.logoStyle !== 'text') {
          // Pre-process logo for better compatibility
          const logoResult = await this.preprocessLogo(config?.logoUrl);
          
          if (logoResult?.success) {
            advancedConfig.image = logoResult?.dataUrl || config?.logoUrl;
            advancedConfig.imageOptions = {
              crossOrigin: "anonymous",
              margin: config?.logoMargin || 8,
              imageSize: (config?.logoSize || 20) / 100,
              hideBackgroundDots: config?.hideBackgroundDots !== false,
              excavate: config?.excavate !== false,
              saveAsBlob: false,
              // Advanced embedding options
              embedInDots: config?.embedInDots || false,
              blendMode: config?.logoBlendMode || config?.blendMode || 'normal',
              opacity: config?.logoOpacity || 1.0,
              borderRadius: config?.logoBorderRadius || 0
            };
          } else {
            console.log('Logo preprocessing failed:', logoResult?.error);
            // Continue without logo rather than failing
          }
        }
      }

      // Generate with qr-code-styling
      const { default: QRCodeStyling } = await import('qr-code-styling');
      const qrCode = new QRCodeStyling(advancedConfig);

      // Create container for generation
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.top = '-9999px';
      container.style.left = '-9999px';
      document.body?.appendChild(container);

      try {
        await qrCode?.append(container);

        // Apply post-processing for advanced effects
        if ((config?.embedText && !config?.logoUrl) || (config?.logoUrl && config?.logoStyle === 'text' && config?.logoText)) {
          await this.embedTextInQR(container, config);
        }

        // Get SVG with embedded content
        const svgElement = container?.querySelector('svg');
        if (svgElement) {
          const svgData = new XMLSerializer()?.serializeToString(svgElement);
          // Convert SVG to data URL instead of blob URL to avoid security issues
          const svgDataUrl = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;

          return {
            success: true,
            dataUrl: svgDataUrl,
            svgData,
            format: 'svg',
            isAdvanced: true,
            isEmbedded: !!(config?.logoUrl || config?.embedText),
            qrCodeInstance: qrCode,
            finalQRContent: qrContent, // Return what was actually embedded
            swappedLogic: true // Flag to indicate we're using swapped URL logic
          };
        }

        throw new Error('SVG generation failed');
      } finally {
        // Cleanup
        try {
          document.body?.removeChild(container);
        } catch (cleanupError) {
          console.log('Container cleanup failed:', cleanupError);
        }
      }
    } catch (error) {
      console.log('Advanced QR generation failed:', error);
      
      // Fallback to canvas-based generation with manual embedding
      return await this.generateCanvasQRWithEmbedding(config);
    }
  }

  /**
   * NEW: Map dot styles to qr-code-styling format
   */
  static mapDotStyle(dotStyle) {
    const styleMap = {
      'square': 'square',
      'rounded': 'rounded',
      'dots': 'dots', // This is the key mapping for visible dots
      'classy': 'classy-rounded'
    };
    return styleMap?.[dotStyle] || 'square';
  }

  /**
   * NEW: Map corner square styles to qr-code-styling format
   */
  static mapCornerSquareStyle(cornerStyle) {
    const styleMap = {
      'square': 'square',
      'extra-rounded': 'extra-rounded', // This is the key mapping for dot-corners
      'dot': 'dot'
    };
    return styleMap?.[cornerStyle] || 'square';
  }

  /**
   * NEW: Map corner dot styles to qr-code-styling format
   */
  static mapCornerDotStyle(cornerDotStyle) {
    const styleMap = {
      'square': 'square',
      'extra-rounded': 'dot', // Round corners should have dot centers
      'dot': 'dot'
    };
    return styleMap?.[cornerDotStyle] || 'square';
  }

  /**
   * Preprocess logo for better compatibility
   */
  static async preprocessLogo(logoUrl) {
    try {
      const { LogoStorageService } = await import('./logoStorageService');
      return await LogoStorageService?.preloadImageForQR?.(logoUrl);
    } catch (error) {
      console.log('Logo preprocessing failed:', error);
      return { success: false, error: error?.message };
    }
  }

  static async embedTextInQR(container, config) {
    try {
      const svg = container?.querySelector('svg');
      if (!svg) return;

      // Calculate center position
      const width = parseInt(svg?.getAttribute('width') || '512');
      const height = parseInt(svg?.getAttribute('height') || '512');
      const centerX = width / 2;
      const centerY = height / 2;

      // Get text to embed - prioritize logo text over embed text
      const textToEmbed = config?.logoText || config?.embedText;
      if (!textToEmbed) return;

      // Create text element with enhanced styling
      const textElement = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      textElement?.setAttribute('x', centerX);
      textElement?.setAttribute('y', centerY);
      textElement?.setAttribute('text-anchor', 'middle');
      textElement?.setAttribute('dominant-baseline', 'central');
      
      // Enhanced styling options
      const textColor = config?.logoTextColor || config?.textColor || config?.foregroundColor || '#000000';
      const fontSize = config?.logoTextSize || config?.textSize || 24;
      const fontFamily = config?.logoTextFont || config?.textFont || 'Arial, sans-serif';
      const fontWeight = config?.logoTextWeight || config?.textWeight || 'bold';
      
      textElement?.setAttribute('fill', textColor);
      textElement?.setAttribute('font-family', fontFamily);
      textElement?.setAttribute('font-size', `${fontSize}px`);
      textElement?.setAttribute('font-weight', fontWeight);
      
      // No text stroke for cleaner look
      // Removed stroke to match user request for no border around text
      
      textElement.textContent = textToEmbed;

      // Add background with same color as QR code background - no border
      if (config?.textBackground !== false) {
        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        
        // Calculate text dimensions more accurately
        const textLength = textToEmbed?.length || 0;
        const estimatedWidth = textLength * (fontSize * 0.6);
        const estimatedHeight = fontSize * 1.2;
        
        const rectX = centerX - (estimatedWidth / 2) - 8;
        const rectY = centerY - (estimatedHeight / 2) - 4;
        const rectWidth = estimatedWidth + 16;
        const rectHeight = estimatedHeight + 8;
        
        rect?.setAttribute('x', rectX);
        rect?.setAttribute('y', rectY);
        rect?.setAttribute('width', rectWidth);
        rect?.setAttribute('height', rectHeight);
        rect?.setAttribute('fill', config?.backgroundColor || '#ffffff');
        rect?.setAttribute('rx', '6');
        rect?.setAttribute('ry', '6');
        
        // No border - clean look with same background as QR code
        // Removed stroke to match user request
        
        svg?.appendChild(rect);
      }

      svg?.appendChild(textElement);
      
      // Add shadow effect for better visibility
      if (config?.textShadow !== false) {
        const shadowElement = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        shadowElement?.setAttribute('x', centerX + 1);
        shadowElement?.setAttribute('y', centerY + 1);
        shadowElement?.setAttribute('text-anchor', 'middle');
        shadowElement?.setAttribute('dominant-baseline', 'central');
        shadowElement?.setAttribute('fill', 'rgba(0,0,0,0.3)');
        shadowElement?.setAttribute('font-family', fontFamily);
        shadowElement?.setAttribute('font-size', `${fontSize}px`);
        shadowElement?.setAttribute('font-weight', fontWeight);
        shadowElement.textContent = textToEmbed;
        
        svg?.insertBefore(shadowElement, textElement);
      }
    } catch (error) {
      console.log('Text embedding failed:', error);
    }
  }

  static async embedLogoOnCanvas(ctx, canvas, config) {
    try {
      // Enhanced logo loading with network error handling
      const logoResult = await this.loadImageWithFallback(config?.logoUrl);
      
      if (!logoResult?.success) {
        console.log('Logo loading failed:', logoResult?.error);
        return; // Gracefully skip logo embedding
      }

      const img = logoResult?.image;
      const logoSize = (config?.logoSize || 20) / 100;
      const logoWidth = canvas?.width * logoSize;
      const logoHeight = (logoWidth * img?.height) / img?.width;
      
      const x = (canvas?.width - logoWidth) / 2;
      const y = (canvas?.height - logoHeight) / 2;

      // Clear background if excavate is enabled
      if (config?.excavate !== false) {
        ctx.fillStyle = config?.backgroundColor || '#ffffff';
        ctx?.fillRect(
          x - (config?.logoMargin || 8),
          y - (config?.logoMargin || 8),
          logoWidth + (config?.logoMargin || 8) * 2,
          logoHeight + (config?.logoMargin || 8) * 2
        );
      }

      // Apply advanced effects
      ctx?.save();
      
      if (config?.logoOpacity && config?.logoOpacity < 1) {
        ctx.globalAlpha = config?.logoOpacity;
      }
      
      // Apply border radius if specified
      if (config?.logoBorderRadius > 0) {
        this.drawRoundedImage(ctx, img, x, y, logoWidth, logoHeight, config?.logoBorderRadius);
      } else {
        ctx?.drawImage(img, x, y, logoWidth, logoHeight);
      }
      
      ctx?.restore();
      
      // Clean up if blob URL was created
      logoResult?.cleanup?.();
    } catch (error) {
      console.log('Canvas logo embedding failed:', error);
    }
  }

  /**
   * Load image with network error handling and fallback using LogoStorageService
   */
  static async loadImageWithFallback(imageUrl, maxRetries = 2) {
    if (!imageUrl) {
      return { success: false, error: 'No image URL provided' };
    }

    try {
      const { LogoStorageService } = await import('./logoStorageService');
      return await LogoStorageService?.preloadImageForQR?.(imageUrl, 15000);
    } catch (error) {
      console.log('Logo loading with service failed:', error);
      return { 
        success: false, 
        error: `Failed to load logo: ${error?.message}` 
      };
    }
  }

  static async embedTextOnCanvas(ctx, canvas, config) {
    try {
      const text = config?.embedText;
      if (!text) return;

      const centerX = canvas?.width / 2;
      const centerY = canvas?.height / 2;
      
      // Advanced text styling
      const fontSize = Math.max(16, Math.min(48, canvas?.width / 12));
      ctx.font = `${config?.textWeight || 'bold'} ${fontSize}px ${config?.textFont || 'Arial, sans-serif'}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Create text background for better visibility
      const metrics = ctx?.measureText(text);
      const textWidth = metrics?.width;
      const textHeight = fontSize;
      
      if (config?.textBackground !== false) {
        // Clear area behind text
        ctx.fillStyle = config?.backgroundColor || '#ffffff';
        ctx?.fillRect(
          centerX - textWidth/2 - 8,
          centerY - textHeight/2 - 4,
          textWidth + 16,
          textHeight + 8
        );
        
        // Add border
        ctx.strokeStyle = config?.textBorderColor || config?.foregroundColor || '#000000';
        ctx.lineWidth = 1;
        ctx?.strokeRect(
          centerX - textWidth/2 - 8,
          centerY - textHeight/2 - 4,
          textWidth + 16,
          textHeight + 8
        );
      }

      // Draw text with effects
      ctx.fillStyle = config?.textColor || config?.foregroundColor || '#000000';
      
      // Add text shadow for better visibility
      if (config?.textShadow !== false) {
        ctx.shadowColor = 'rgba(0,0,0,0.3)';
        ctx.shadowBlur = 2;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;
      }
      
      ctx?.fillText(text, centerX, centerY);
      
      // Reset shadow
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
    } catch (error) {
      console.log('Canvas text embedding failed:', error);
    }
  }

  static drawRoundedImage(ctx, img, x, y, width, height, radius) {
    ctx?.save();
    ctx?.beginPath();
    ctx?.moveTo(x + radius, y);
    ctx?.lineTo(x + width - radius, y);
    ctx?.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx?.lineTo(x + width, y + height - radius);
    ctx?.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx?.lineTo(x + radius, y + height);
    ctx?.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx?.lineTo(x, y + radius);
    ctx?.quadraticCurveTo(x, y, x + radius, y);
    ctx?.closePath();
    ctx?.clip();
    ctx?.drawImage(img, x, y, width, height);
    ctx?.restore();
  }

  static async applyRoundedEffects(ctx, canvas, config) {
    // Apply rounded corner effects to QR code modules
    // This is a complex operation that would modify the QR pattern
    // For now, we'll focus on the logo and text embedding
    try {
      // Advanced rounding logic would go here
      // This would involve analyzing the QR pattern and applying rounded corners
      console.log('Applying rounded effects...');
    } catch (error) {
      console.log('Rounded effects failed:', error);
    }
  }

  // Generate QR with custom shapes (like the example image)
  static async generateCustomShapeQR(config) {
    try {
      const advancedConfig = {
        ...config,
        dotsOptions: {
          color: config?.foregroundColor || "#000000",
          type: "dots", // Use dots instead of squares for better logo integration
          gradient: config?.gradient
        },
        cornersSquareOptions: {
          color: config?.foregroundColor || "#000000",
          type: "extra-rounded", // Rounded corners like in your example
        },
        cornersDotOptions: {
          color: config?.foregroundColor || "#000000",
          type: "dot", // Circular corner dots
        }
      };

      return await this.generateAdvancedQR(advancedConfig);
    } catch (error) {
      console.log('Custom shape QR generation failed:', error);
      return { success: false, error: error?.message };
    }
  }

  /**
   * CRITICAL FIX: Enhanced QR generation with better error handling - FORCE TRACKABLE URLs
   */
  static async generateQRWithLogo(content, options = {}) {
    try {
      const {
        size = 400,
        logoUrl,
        logoSize = 80,
        errorCorrectionLevel = 'M',
        style = {},
        format = 'png',
        shortCode = null // NEW: Accept shortCode parameter
      } = options;

      // CRITICAL FIX: Always use trackable URL if shortCode is provided
      let finalContent = content;
      if (shortCode) {
        try {
          const { TrackingUrlService } = await import('./trackingUrlService');
          finalContent = TrackingUrlService?.generateTrackingUrl?.(shortCode) || finalContent;
        } catch {
          // ignore, keep fallback
        }
        console.log(`LOGO QR OVERRIDE: Using trackable URL ${finalContent} instead of ${content}`);
      }

      // Generate base QR code first with proper style mapping
      const { default: QRCodeStyling } = await import('qr-code-styling');
      const qrCode = new QRCodeStyling({
        width: size,
        height: size,
        data: finalContent, // CRITICAL: Use trackable URL when available
        qrOptions: {
          errorCorrectionLevel
        },
        imageOptions: {
          hideBackgroundDots: true,
          imageSize: logoSize / size,
          margin: 8
        },
        dotsOptions: {
          color: style?.foregroundColor || '#000000',
          // FIXED: Apply proper dotStyle mapping
          type: this.mapDotStyle(style?.dotStyle || style?.dotType || 'rounded')
        },
        backgroundOptions: {
          color: style?.backgroundColor || '#FFFFFF'
        },
        cornersSquareOptions: {
          color: style?.cornerSquareColor || style?.foregroundColor || '#000000',
          // FIXED: Apply proper corner style mapping
          type: this.mapCornerSquareStyle(style?.cornerSquareType || style?.cornerStyle || 'extra-rounded')
        },
        cornersDotOptions: {
          color: style?.cornerDotColor || style?.foregroundColor || '#000000',
          // FIXED: Apply proper corner dot mapping
          type: this.mapCornerDotStyle(style?.cornerDotType || style?.cornerStyle || 'dot')
        }
      });

      // Handle logo with enhanced error handling
      if (logoUrl) {
        try {
          const logoResult = await this.preprocessLogo(logoUrl);
          
          if (logoResult?.success) {
            qrCode?.update({
              image: logoResult?.dataUrl
            });
          } else {
            // Generate QR without logo and provide user feedback
            console.warn('Logo loading failed, generating QR without logo:', logoResult?.error);
            return {
              success: true,
              qrDataUrl: await this.getQRDataUrl(qrCode, format),
              warning: `QR generated without logo: ${logoResult?.error}`,
              trackableUrl: finalContent,
              originalContent: content,
              isTrackable: !!shortCode
            };
          }
        } catch (logoError) {
          console.warn('Logo processing failed:', logoError);
          // Continue without logo
          return {
            success: true,
            qrDataUrl: await this.getQRDataUrl(qrCode, format),
            warning: 'QR generated without logo due to image loading issues',
            trackableUrl: finalContent,
            originalContent: content,
            isTrackable: !!shortCode
          };
        }
      }

      // Generate final QR data URL
      const qrDataUrl = await this.getQRDataUrl(qrCode, format);

      return {
        success: true,
        qrDataUrl,
        logoUsed: !!logoUrl,
        size: { width: size, height: size },
        trackableUrl: finalContent,
        originalContent: content,
        isTrackable: !!shortCode
      };

    } catch (error) {
      return {
        success: false,
        error: `QR generation failed: ${error?.message}`,
        fallback: 'Try generating without logo or with different settings'
      };
    }
  }

  /**
   * CRITICAL FIX: Ensure ALL QR codes use trackable URLs for proper analytics
   */
  static async generateQRCodeWithTracking(
    trackableUrl,  // This is the tracking URL that goes into the QR code
    qrCodeId = null,
    shortCode = null,
    options = {}
  ) {
    try {
      // CRITICAL FIX: Always validate and ensure trackable URL
      let finalTrackableUrl = trackableUrl;
      
      // If trackable URL is not provided but shortCode exists, generate it
      if (!trackableUrl && shortCode) {
        try {
          const { TrackingUrlService } = await import('./trackingUrlService');
          finalTrackableUrl = TrackingUrlService?.generateTrackingUrl?.(shortCode) || finalTrackableUrl;
        } catch {
          // ignore
        }
        console.log(`GENERATED TRACKABLE URL: ${finalTrackableUrl}`);
      }
      
      // Double-check: If shortCode exists but URL isn't a tracking URL, correct it to a tracking URL
      if (shortCode && finalTrackableUrl && !finalTrackableUrl?.includes('/qr/')) {
        try {
          const { TrackingUrlService } = await import('./trackingUrlService');
          finalTrackableUrl = TrackingUrlService?.generateTrackingUrl?.(shortCode) || finalTrackableUrl;
          console.log(`CORRECTED TRACKABLE URL: ${finalTrackableUrl}`);
        } catch {
          // ignore
        }
      }

      if (!finalTrackableUrl) {
        return {
          success: false,
          error: 'Cannot generate QR without trackable URL - analytics tracking would fail',
          trackableUrl: null
        };
      }

      console.log(`QR GENERATION: Embedding trackable URL: ${finalTrackableUrl}`);
      console.log(`This URL will record analytics before redirecting to content`);

      // CRITICAL FIX: Use the existing generateBasicQRWithTracking method
      const qrResult = await this.generateBasicQRWithTracking(finalTrackableUrl, {
        width: options?.size || 512,
        height: options?.size || 512,
        foregroundColor: options?.foregroundColor || '#000000',
        backgroundColor: options?.backgroundColor || '#ffffff',
        errorCorrectionLevel: options?.errorCorrectionLevel || 'H',
        shortCode: shortCode, // CRITICAL: Pass shortCode for validation
        
        // Logo options
        logoUrl: options?.logoUrl,
        logoSize: options?.logoSize || 20,
        logoMargin: options?.logoMargin || 8,
        hideBackgroundDots: options?.hideBackgroundDots !== false,
        excavate: options?.excavate !== false,
        
        // Logo integration style
        logoStyle: options?.logoStyle || 'embedded',
        
        // Text integration options (for logo text mode)
        logoText: options?.logoText,
        logoTextSize: options?.logoTextSize || 24,
        logoTextWeight: options?.logoTextWeight || 'bold',
        logoTextColor: options?.logoTextColor,
        logoTextFont: options?.logoTextFont || 'Arial',
        
        // Advanced logo options
        logoOpacity: options?.logoOpacity || 1.0,
        logoBlendMode: options?.logoBlendMode || 'normal',
        
        // Legacy text embedding options (for direct text embedding)
        embedText: options?.embedText,
        textSize: options?.textSize || 24,
        textWeight: options?.textWeight || 'bold',
        textColor: options?.textColor,
        
        // Pattern styling
        dotStyle: options?.dotStyle || 'square',
        cornerStyle: options?.cornerStyle || 'square',
        cornerDotStyle: options?.cornerDotStyle || 'square'
      });

      if (qrResult?.success) {
        return {
          ...qrResult,
          trackableUrl: finalTrackableUrl,           // What's embedded in QR code
          trackingEnabled: true,
          shortCode,
          qrCodeId
        };
      } else {
        return {
          success: false,
          error: qrResult?.error || 'QR generation failed',
          trackableUrl: finalTrackableUrl
        };
      }

    } catch (error) {
      return {
        success: false,
        error: `Tracking QR generation failed: ${error?.message}`,
        trackableUrl: null
      };
    }
  }

  /**
   * UPDATED: Generate basic QR with SWAPPED URL logic
   */
  static async generateBasicQRWithTracking(trackableUrl, config) {
    try {
      // SWAPPED LOGIC: Use the URL as-is from database (should already be trackable)
      let finalUrl = trackableUrl;
      
      console.log(`🔄 SWAPPED QR GENERATION: Using URL as-is: ${finalUrl}`);
      
      // Check if we need advanced features (text integration, advanced logo options)
      const needsAdvancedFeatures = config?.logoStyle === 'text' || 
                                   config?.logoText || 
                                   config?.embedText ||
                                   config?.logoOpacity !== 1.0 ||
                                   config?.logoBlendMode !== 'normal';
      
      if (needsAdvancedFeatures) {
        console.log('🔄 Using advanced QR generation for text integration');
        console.log('🔄 finalUrl:', finalUrl);
        console.log('🔄 config:', config);
        // Use the advanced QR generation for text integration
        const advancedConfig = {
          content: finalUrl,
          logoUrl: config?.logoUrl,
          logoStyle: config?.logoStyle || 'embedded',
          logoText: config?.logoText,
          logoTextSize: config?.logoTextSize,
          logoTextWeight: config?.logoTextWeight,
          logoTextColor: config?.logoTextColor,
          logoTextFont: config?.logoTextFont,
          logoOpacity: config?.logoOpacity,
          logoBlendMode: config?.logoBlendMode,
          embedText: config?.embedText,
          textSize: config?.textSize,
          textWeight: config?.textWeight,
          textColor: config?.textColor,
          foregroundColor: config?.foregroundColor,
          backgroundColor: config?.backgroundColor,
          errorCorrectionLevel: config?.errorCorrectionLevel,
          size: config?.width || 512
        };
        
        console.log('🔄 advancedConfig:', advancedConfig);
        return await this.generateAdvancedQR(advancedConfig);
      }
      
      const { default: QRCodeStyling } = await import('qr-code-styling');
      
      const qrConfig = {
        width: config?.width || 512,
        height: config?.height || 512,
        type: "svg",
        data: finalUrl, // Use URL as-is - database migration handles the logic
        dotsOptions: {
          color: config?.foregroundColor || '#000000',
          type: this.mapDotStyle(config?.dotStyle || 'square')
        },
        backgroundOptions: {
          color: config?.backgroundColor || '#ffffff'
        },
        cornersSquareOptions: {
          color: config?.foregroundColor || '#000000',
          type: this.mapCornerSquareStyle(config?.cornerStyle || 'square')
        },
        cornersDotOptions: {
          color: config?.foregroundColor || '#000000',
          type: this.mapCornerDotStyle(config?.cornerDotStyle || 'square')
        },
        qrOptions: {
          errorCorrectionLevel: config?.errorCorrectionLevel || 'H'
        }
      };

      // Add logo if present
      if (config?.logoUrl) {
        qrConfig.image = config?.logoUrl;
        qrConfig.imageOptions = {
          crossOrigin: "anonymous",
          margin: config?.logoMargin || 8,
          imageSize: (config?.logoSize || 20) / 100,
          hideBackgroundDots: config?.hideBackgroundDots !== false,
          excavate: config?.excavate !== false,
          saveAsBlob: false
        };
      }

      const qrCode = new QRCodeStyling(qrConfig);
      
      // Create container for QR code
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.top = '-9999px';
      container.style.left = '-9999px';
      document.body?.appendChild(container);
      
      try {
        await qrCode?.append(container);
        
        // Get the SVG element
        const svgElement = container?.querySelector('svg');
        
        if (svgElement) {
          const svgData = new XMLSerializer()?.serializeToString(svgElement);
          
          // Convert SVG to data URL instead of blob URL to avoid security issues
          const svgDataUrl = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
          
          return {
            success: true,
            dataUrl: svgDataUrl,
            svgData,
            format: 'svg',
            qrCodeInstance: qrCode,
            isEmbedded: !!config?.logoUrl,
            trackableContent: finalUrl,
            swappedLogic: true, // Flag indicating we're using swapped URL logic
            finalLibraryInput: qrConfig?.data // What was actually passed to the library
          };
        } else {
          throw new Error('Failed to generate SVG');
        }
      } catch (error) {
        throw error;
      } finally {
        // Cleanup container
        try {
          document.body?.removeChild(container);
        } catch (cleanupError) {
          console.log('Container cleanup failed:', cleanupError);
        }
      }
    } catch (error) {
      throw new Error(`Basic QR generation failed: ${error?.message}`);
    }
  }

  static async generateCanvasQRWithEmbedding(config) {
    try {
      // SWAPPED LOGIC: Use URL as-is from database
      let qrContent = config?.data;
      
      console.log(`🔄 CANVAS SWAPPED LOGIC: Using URL as-is: "${qrContent}"`);

      const { default: QRCode } = await import('qrcode');
      const canvas = document.createElement('canvas');
      const size = Math.min(config?.width || 512, 1024);
      
      canvas.width = size;
      canvas.height = size;

      // Use the URL as-is - database migration handles the swapped logic
      await QRCode?.toCanvas(canvas, qrContent, {
        width: size,
        color: {
          dark: config?.foregroundColor || '#000000',
          light: config?.backgroundColor || '#ffffff'
        },
        errorCorrectionLevel: config?.errorCorrectionLevel || 'H',
        margin: 2
      });

      const ctx = canvas?.getContext('2d');

      // Embed text like "TAYLOR" if specified
      if (config?.embedText) {
        await this.embedTextOnCanvas(ctx, canvas, config);
      }

      // Embed logo if specified - with enhanced error handling
      if (config?.logoUrl) {
        try {
          await this.embedLogoOnCanvas(ctx, canvas, config);
        } catch (logoError) {
          console.log('Logo embedding failed, continuing without logo:', logoError);
          // Don't fail the entire process if logo fails
        }
      }

      // Apply post-processing effects
      if (config?.cornerStyle === 'rounded' || config?.dotStyle === 'rounded') {
        await this.applyRoundedEffects(ctx, canvas, config);
      }

      const dataUrl = canvas?.toDataURL('image/png', 0.9);
      
      return {
        success: true,
        dataUrl,
        canvas,
        format: 'png',
        isAdvanced: true,
        isEmbedded: !!(config?.logoUrl || config?.embedText),
        fallback: 'canvas',
        finalQRContent: qrContent, // Return what was actually embedded
        swappedLogic: true // Flag indicating we're using swapped URL logic
      };
    } catch (error) {
      console.log('Canvas QR generation failed:', error);
      return {
        success: false,
        error: `Failed to generate QR code: ${error?.message}`,
        fallback: 'basic'
      };
    }
  }

  /**
   * Helper method to get QR data URL with error handling
   */
  static async getQRDataUrl(qrCode, format) {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      
      qrCode.append(canvas);
      
      // Add timeout for canvas generation
      const timeout = setTimeout(() => {
        reject(new Error('QR code generation timeout'));
      }, 10000);

      try {
        // Wait for rendering to complete
        setTimeout(() => {
          try {
            const dataUrl = canvas.toDataURL(`image/${format}`, 0.9);
            clearTimeout(timeout);
            resolve(dataUrl);
          } catch (canvasError) {
            clearTimeout(timeout);
            reject(new Error(`Canvas export failed: ${canvasError?.message}`));
          }
        }, 100);
      } catch (error) {
        clearTimeout(timeout);
        reject(error);
      }
    });
  }

  /**
   * CRITICAL FIX: Enhanced QR generation with trackable URL support - FORCE TRACKING
   */
  static async generateQRWithTrackableUrl(content, shortCode, options = {}) {
    try {
      // CRITICAL: Always use trackable URL for QR generation - FORCE IT
      let trackableUrl = null;
      
      if (shortCode) {
        try {
          const { TrackingUrlService } = await import('./trackingUrlService');
          trackableUrl = TrackingUrlService?.generateTrackingUrl?.(shortCode);
        } catch {
          trackableUrl = null;
        }
      } else {
        // Try to import tracking service if shortCode is missing
        try {
          const { TrackingUrlService } = await import('./trackingUrlService');
          trackableUrl = TrackingUrlService?.generateTrackingUrl(shortCode);
        } catch (importError) {
          console.warn('Could not import TrackingUrlService:', importError);
        }
      }
      
      if (!trackableUrl) {
        throw new Error('Failed to generate trackable URL - this is required for analytics');
      }

      console.log(`TRACKABLE QR: Using ${trackableUrl} instead of ${content} for proper analytics`);

      const {
        size = 400,
        logoUrl,
        logoSize = 80,
        errorCorrectionLevel = 'M',
        style = {},
        format = 'png'
      } = options;

      // Generate QR with trackable URL instead of original content
      const { default: QRCodeStyling } = await import('qr-code-styling');
      const qrCode = new QRCodeStyling({
        width: size,
        height: size,
        data: trackableUrl, // CRITICAL: Always use trackable URL here
        qrOptions: {
          errorCorrectionLevel
        },
        imageOptions: {
          hideBackgroundDots: true,
          imageSize: logoSize / size,
          margin: 8
        },
        dotsOptions: {
          color: style?.foregroundColor || '#000000',
          type: this.mapDotStyle(style?.dotStyle || style?.dotType || 'rounded')
        },
        backgroundOptions: {
          color: style?.backgroundColor || '#FFFFFF'
        },
        cornersSquareOptions: {
          color: style?.cornerSquareColor || style?.foregroundColor || '#000000',
          type: this.mapCornerSquareStyle(style?.cornerSquareType || style?.cornerStyle || 'extra-rounded')
        },
        cornersDotOptions: {
          color: style?.cornerDotColor || style?.foregroundColor || '#000000',
          type: this.mapCornerDotStyle(style?.cornerDotType || style?.cornerStyle || 'dot')
        }
      });

      // Handle logo with enhanced error handling
      if (logoUrl) {
        try {
          const logoResult = await this.preprocessLogo(logoUrl);
          
          if (logoResult?.success) {
            qrCode?.update({
              image: logoResult?.dataUrl
            });
          } else {
            console.warn('Logo loading failed, generating QR without logo:', logoResult?.error);
            return {
              success: true,
              qrDataUrl: await this.getQRDataUrl(qrCode, format),
              warning: `QR generated without logo: ${logoResult?.error}`,
              trackableUrl,
              originalContent: content,
              analyticsEnabled: true,
              trackingMessage: 'QR uses trackable URL - analytics will work properly'
            };
          }
        } catch (logoError) {
          console.warn('Logo processing failed:', logoError);
          return {
            success: true,
            qrDataUrl: await this.getQRDataUrl(qrCode, format),
            warning: 'QR generated without logo due to image loading issues',
            trackableUrl,
            originalContent: content,
            analyticsEnabled: true,
            trackingMessage: 'QR uses trackable URL - analytics will work properly'
          };
        }
      }

      // Generate final QR data URL
      const qrDataUrl = await this.getQRDataUrl(qrCode, format);

      return {
        success: true,
        qrDataUrl,
        logoUsed: !!logoUrl,
        size: { width: size, height: size },
        trackableUrl,
        originalContent: content,
        isTrackable: true,
        analyticsEnabled: true,
        trackingMessage: 'QR code successfully generated with trackable URL for analytics'
      };

    } catch (error) {
      return {
        success: false,
        error: `Trackable QR generation failed: ${error?.message}`,
        fallback: 'Try generating without advanced features',
        analyticsEnabled: false,
        isTrackable: false,
        trackingMessage: 'QR generation failed - direct URLs cannot be tracked'
      };
    }
  }
}

export default AdvancedQRGeneratorService;